import { GLOBAL } from '@lumapps/translations';
import { UserDirectoryTemplateComponentType } from '@lumapps/user-directory/types';
import { DEFAULT_API_PROFILE_FIELD_PATTERNS } from '@lumapps/user/constants';
import { FroalaSettings } from '@lumapps/wrex-legacy-froala/constants';

import { USER_DIRECTORY_BACK_OFFICE } from './keys';
import { TemplateFieldComponentAlias } from './types';
import type { TemplateFieldComponent } from './types';

export const USER_DIRECTORY_ADMIN_FF = 'user-directory-react-back-office';

/**
 * The `boundMap.name` used when a field is bound to a pattern the user typed themselves rather than
 * to one of the default fields below. Kept byte-identical to the legacy admin's sentinel so a field
 * configured in either admin round-trips through the other.
 */
export const API_PROFILE_FIELD_CUSTOM = 'API_PROFILE_FIELD_CUSTOM';

/** A default user profile field offered in the template field form's "Field" selector. */
export interface DefaultUserFieldDefinition {
    /** Persisted as `properties.boundMap.name`. Doubles as the legacy translation key for the field. */
    name: string;
    /** Persisted as `properties.boundMap.text` — the apiProfile path the field reads from. */
    pattern: string;
    /** Translation key for the option's label. */
    labelKey: GLOBAL | USER_DIRECTORY_BACK_OFFICE;
    /** Input component the field is rendered, edited and filtered with. */
    component: TemplateFieldComponent;
}

/**
 * The default user fields a template field can be bound to.
 *
 * Held in the frontend because no backend endpoint exposes a catalogue to the admin editor, and kept
 * in the same order and with the same names as the legacy admin's `Config.AVAILABLE_API_PROFILE_FIELD`
 * so both admins resolve a saved field to the same entry.
 *
 * `component` has no legacy equivalent — legacy asked the user to pick a component even for a bound
 * field. It is what lets a bound field derive its component instead, so Manager is a user picker
 * rather than free text.
 */
export const DEFAULT_USER_FIELDS: DefaultUserFieldDefinition[] = [
    {
        name: 'API_PROFILE_FIELD_TITLE',
        pattern: DEFAULT_API_PROFILE_FIELD_PATTERNS.title,
        labelKey: GLOBAL.TITLE,
        component: UserDirectoryTemplateComponentType.inputText,
    },
    {
        name: 'API_PROFILE_FIELD_PRIMARY_ORGANIZATIONS_NAME',
        pattern: DEFAULT_API_PROFILE_FIELD_PATTERNS.primaryOrganizationsName,
        labelKey: GLOBAL.ORGANIZATION,
        component: UserDirectoryTemplateComponentType.inputText,
    },
    {
        name: 'API_PROFILE_FIELD_PRIMARY_ORGANIZATIONS_LOCATION',
        pattern: DEFAULT_API_PROFILE_FIELD_PATTERNS.primaryOrganizationsLocation,
        labelKey: GLOBAL.LOCATION,
        component: UserDirectoryTemplateComponentType.inputText,
    },
    {
        name: 'API_PROFILE_FIELD_PHONES_WORK',
        pattern: DEFAULT_API_PROFILE_FIELD_PATTERNS.phonesWork,
        labelKey: USER_DIRECTORY_BACK_OFFICE.WORK,
        component: UserDirectoryTemplateComponentType.inputText,
    },
    {
        name: 'API_PROFILE_FIELD_PHONES_PERSONAL',
        pattern: DEFAULT_API_PROFILE_FIELD_PATTERNS.phonesPersonal,
        labelKey: GLOBAL.MOBILE,
        component: UserDirectoryTemplateComponentType.inputText,
    },
    {
        name: 'API_PROFILE_FIELD_EMAILS_PRIMARY',
        pattern: DEFAULT_API_PROFILE_FIELD_PATTERNS.emailsPrimary,
        labelKey: GLOBAL.EMAIL,
        component: UserDirectoryTemplateComponentType.inputText,
    },
    {
        name: 'API_PROFILE_FIELD_RELATIONS_MANAGER',
        pattern: DEFAULT_API_PROFILE_FIELD_PATTERNS.relationsManager,
        labelKey: GLOBAL.MANAGER,
        // A manager is a reference to another user, not a free-text value.
        component: TemplateFieldComponentAlias.user,
    },
];

/**
 * Icons for the fields seeded into a brand new directory's template, keyed by catalogue name.
 * A field absent from this map is not seeded — only the six legacy default fields are.
 */
export const DEFAULT_TEMPLATE_FIELD_ICONS: Record<string, string> = {
    API_PROFILE_FIELD_TITLE: 'bank',
    API_PROFILE_FIELD_PRIMARY_ORGANIZATIONS_NAME: 'clipboard-account',
    API_PROFILE_FIELD_PRIMARY_ORGANIZATIONS_LOCATION: 'city',
    API_PROFILE_FIELD_PHONES_WORK: 'phone',
    API_PROFILE_FIELD_PHONES_PERSONAL: 'phone',
    API_PROFILE_FIELD_EMAILS_PRIMARY: 'email',
};

/** Maps the UI's component type alias (e.g. 'user') to the backend component type consumed by the API. */
export const TEMPLATE_COMPONENT_TO_TYPE: Record<TemplateFieldComponent, UserDirectoryTemplateComponentType> = {
    [UserDirectoryTemplateComponentType.inputText]: UserDirectoryTemplateComponentType.inputText,
    [UserDirectoryTemplateComponentType.inputDate]: UserDirectoryTemplateComponentType.inputDate,
    [UserDirectoryTemplateComponentType.inputNumber]: UserDirectoryTemplateComponentType.inputNumber,
    [UserDirectoryTemplateComponentType.metadata]: UserDirectoryTemplateComponentType.metadata,
    [UserDirectoryTemplateComponentType.inputSelect]: UserDirectoryTemplateComponentType.inputSelect,
    [TemplateFieldComponentAlias.user]: UserDirectoryTemplateComponentType.inputSelect,
};

/** Maps each template component type to the translation key for its display label. */
export const TEMPLATE_COMPONENT_TRANSLATION_KEYS: Record<TemplateFieldComponent, GLOBAL> = {
    [UserDirectoryTemplateComponentType.inputText]: GLOBAL.TEXT,
    [UserDirectoryTemplateComponentType.inputDate]: GLOBAL.DATE,
    [UserDirectoryTemplateComponentType.inputNumber]: GLOBAL.NUMBER,
    [UserDirectoryTemplateComponentType.metadata]: GLOBAL.METADATA,
    [UserDirectoryTemplateComponentType.inputSelect]: GLOBAL.USER,
    [TemplateFieldComponentAlias.user]: GLOBAL.USER,
};

/** Table column ids the user directory list can be sorted on. */
export type SortableColumnId = 'name' | 'updatedAt';

/**
 * Maps a sortable table column id to the field name `content/list` accepts in `sortOrder`.
 *
 * The two differ on purpose: the Name column is `name` in the UI (matching its cell id and data
 * attributes, and reading better in the URL) while the backend whitelists the content field `title`.
 * `content_list_manager._get_valid_sort_order_values()` raises `BadRequestException` on any field it
 * does not whitelist, so a column absent from this map must resolve to no sort at all rather than be
 * forwarded and fail the whole list request.
 */
export const SORT_FIELD_BY_COLUMN: Record<SortableColumnId, string> = {
    name: 'title',
    updatedAt: 'updatedAt',
};

/** Whether a table column id is one this list can sort on — narrows before indexing the map above. */
export const isSortableColumnId = (key: string): key is SortableColumnId => key in SORT_FIELD_BY_COLUMN;

/**
 * Regex persisted in a template field's `validationRegex` when the "Email pattern" preset is selected.
 * Kept byte-identical to the legacy admin's `USER_DIRECTORY_REGEX_DEFAULT` so a field configured in
 * either admin round-trips through the other.
 */
export const EMAIL_VALIDATION_PATTERN =
    "/^[-a-z0-9~!$%^&*_=+}{'?]+(.[-a-z0-9~!$%^&*_=+}{'?]+)*@([a-z0-9_][-a-z0-9_]*(.[-a-z0-9_]+)*.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}))(:[0-9]{1,5})?$/i";

export const FROALA_CONFIG = {
    ...FroalaSettings,
    // Disable the character counter display.
    charCounterCount: false,
    // Minimum height of the editor in pixels.
    heightMin: 150,
    // Prevent execution of scripts inside the HTML content.
    htmlExecuteScripts: false,
    // Allow the editor to create multiple lines on Enter.
    multiLine: true,
    // Strip formatting when pasting content.
    pastePlain: true,
    // Disable the quick insert button that appears on empty lines.
    quickInsertEnabled: false,
    // Do not keep the toolbar visible when scrolling.
    toolbarSticky: false,
};
